import { ulBriefcase01 } from '@faceup/icons/ulBriefcase01'
import { ulBuilding07 } from '@faceup/icons/ulBuilding07'
import { ulBuilding08 } from '@faceup/icons/ulBuilding08'
import { ulDataflow02 } from '@faceup/icons/ulDataflow02'
import { ulFolder } from '@faceup/icons/ulFolder'
import { ulGraduationHat02 } from '@faceup/icons/ulGraduationHat02'
import { ulMail01 } from '@faceup/icons/ulMail01'
import { ulPhoneCall01 } from '@faceup/icons/ulPhoneCall01'
import { ulPhoneCall02 } from '@faceup/icons/ulPhoneCall02'
import { ulPhoneIncoming01 } from '@faceup/icons/ulPhoneIncoming01'
import { ulTable } from '@faceup/icons/ulTable'
import { ulTool01 } from '@faceup/icons/ulTool01'
import { ulUsers01 } from '@faceup/icons/ulUsers01'
import {
  AdminMenu,
  type AdminMenuItem,
  type AdminMenuLogo,
  PageContentSkeleton,
  AdminLayout as UiAdminLayout,
  ucAiPhone,
} from '@faceup/ui'
import { FuAdminRoleType } from '@faceup/utils'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'
import { routes } from '../hooks/useRoutes'
import HeaderProfile from './HeaderProfile'
import RegionSelect from './RegionSelect'

const logo: AdminMenuLogo = {
  src: '/logo/U-logo.svg',
  name: 'Kredenc',
  backgroundColor: '#fd9f13',
  link: routes => routes.home(),
}

const fragments = {
  FuAdminLayout_admin: graphql(`
    fragment FuAdminLayout_admin on FuAdmin {
      id
      name
      role
      companyCountries
      schoolCountries
      ...HeaderProfile_user
    }
  `),
}

type Props = {
  children?: React.ReactNode
  logout: () => void
  viewer: FragmentType<typeof fragments.FuAdminLayout_admin> | null
}

const AdminLayout = ({ viewer: _viewer, logout, children }: Props) => {
  const viewer = getFragmentData(fragments.FuAdminLayout_admin, _viewer)
  const isManagerOrAdmin =
    viewer?.role === FuAdminRoleType.Manager || viewer?.role === FuAdminRoleType.Admin
  const isAdmin = viewer?.role === FuAdminRoleType.Admin
  const isPsychologist = viewer?.role === FuAdminRoleType.Psychologist
  const isIntegrationDeveloper = viewer?.role === FuAdminRoleType.IntegrationDeveloper

  // mock loading instead of spinner
  if (!viewer?.id) {
    return (
      <UiAdminLayout menu={<AdminMenu label='Main navigation' logo={logo} items={[]} />}>
        <PageContentSkeleton />
      </UiAdminLayout>
    )
  }

  const items: AdminMenuItem[] = [
    {
      key: 'organizations',
      label: 'Organizations',
      icon: ulBuilding07,
      secondary: {
        sections: [
          {
            key: 'organizations-institutions',
            items: [
              {
                key: 'companies',
                label: 'Companies',
                icon: ulBuilding08,
                link: routes => routes.companies(),
                match: [routes.institution],
                visible: Boolean(viewer.companyCountries?.length) && isManagerOrAdmin,
              },
              {
                key: 'schools',
                label: 'Schools',
                icon: ulGraduationHat02,
                link: routes => routes.schools(),
                match: [routes.institution],
                visible:
                  Boolean(viewer.schoolCountries?.length) && (isManagerOrAdmin || isPsychologist),
              },
              {
                key: 'partners',
                label: 'Partners',
                icon: ulUsers01,
                link: routes => routes.partners(),
                match: [routes.partner],
                visible: Boolean(viewer.companyCountries?.length) && isManagerOrAdmin,
              },
            ],
          },
        ],
      },
    },
    {
      key: 'cases',
      label: 'Cases',
      icon: ulBriefcase01,
      link: routes => routes.reports(),
      match: [routes.report],
      visible: isPsychologist,
    },
    {
      key: 'materials',
      label: 'Materials',
      icon: ulFolder,
      link: routes => routes.materials(),
      visible: isManagerOrAdmin,
    },
    {
      key: 'hotlines',
      label: 'Hotlines',
      icon: ulPhoneCall01,
      secondary: {
        sections: [
          {
            key: 'hotlines-configuration',
            items: [
              {
                key: 'hotlines-list',
                label: 'Hotlines',
                icon: ulPhoneIncoming01,
                link: routes => routes.hotlines(),
                visible: isManagerOrAdmin,
              },
              {
                key: 'vonage-hotlines',
                label: 'Vonage hotlines',
                icon: ulPhoneCall02,
                link: routes => routes.vonageHotlines(),
                visible: isManagerOrAdmin,
              },
              {
                key: 'ai-hotline-system-instructions',
                label: 'AI hotline config',
                icon: ucAiPhone,
                link: routes => routes.aiHotlineSystemInstructions(),
                visible: isManagerOrAdmin,
              },
            ],
          },
        ],
      },
    },
    {
      key: 'tools',
      label: 'Tools',
      icon: ulTool01,
      secondary: {
        sections: [
          {
            key: 'tools-internal',
            items: [
              {
                key: 'send-emails',
                label: 'Send emails',
                icon: ulMail01,
                link: routes => routes.sendEmails(),
                visible: isAdmin,
              },
              {
                key: 'global-tables',
                label: 'Global tables',
                icon: ulTable,
                link: routes => routes.globalTables(),
                visible: isAdmin,
              },
              {
                key: 'integration-http-logs',
                label: 'Integration HTTP logs',
                icon: ulDataflow02,
                link: routes => routes.integrationHttpLogs(),
                visible: isAdmin || isIntegrationDeveloper,
              },
            ],
          },
        ],
      },
    },
  ]

  return (
    <UiAdminLayout menu={<AdminMenu label='Main navigation' logo={logo} items={items} />}>
      <div className='sticky top-0 z-[1] flex justify-end gap-3 border-colorBorderSecondary border-b bg-white p-3'>
        <RegionSelect />
        <HeaderProfile logout={() => logout()} viewer={viewer} />
      </div>
      {children}
    </UiAdminLayout>
  )
}

export default AdminLayout
